/**
 * CountryMatchPanel — Algorithm's top 3 country recommendations with signal breakdown.
 *
 * For each country: overall score (0-100), then 5 signal bars showing each
 * signal's normalized score and weighted contribution. OECD proxy signal is
 * honestly labeled (proxy: true) so the user doesn't mistake supply-side data
 * for demand-side data.
 */
import React from "react";

const FLAGS = { US: "🇺🇸", GB: "🇬🇧", IN: "🇮🇳" };
const COUNTRY_NAMES = { US: "United States", GB: "United Kingdom", IN: "India" };

const SIGNAL_LABELS = {
  gdp_pc_ppp_usd: "GDP per capita (PPP)",
  secure_servers_per_million: "Digital infrastructure (B-READY proxy)",
  internet_users_pct: "Internet penetration",
  epi_score: "English proficiency (EF EPI 2024)",
  saas_proxy_ict_export_pct: "B2B SaaS supply (ICT exports proxy)",
};

const SignalBar = ({ label, score, weight, raw, units, source, year, proxy }) => {
  const pct = Math.max(0, Math.min(100, score || 0));
  const contribution = (pct * (weight || 0)).toFixed(1);
  return (
    <div data-testid={`signal-bar-${label}`} className="space-y-0.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-zinc-700">
          {SIGNAL_LABELS[label] || label}
          {proxy && (
            <span
              className="ml-1.5 text-[10px] font-mono bg-amber-50 text-amber-700 px-1 border border-amber-300"
              title="This signal is a proxy — interpret weakly. See Day 2 PoC report for discussion."
            >
              proxy
            </span>
          )}
        </span>
        <span className="font-mono text-zinc-500">
          {raw?.toLocaleString?.(undefined, { maximumFractionDigits: 1 }) ?? "?"}
        </span>
      </div>
      <div className="h-1.5 bg-zinc-100 overflow-hidden">
        <div
          className={`h-full ${proxy ? "bg-amber-400" : "bg-[#7C3AED]"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-[10px] text-zinc-400 font-mono">
        <span>
          norm={pct.toFixed(0)} · weight={((weight || 0) * 100).toFixed(0)}% · contrib={contribution}
        </span>
        <span>
          {source} {year && `· ${year}`}
        </span>
      </div>
    </div>
  );
};

const CountryCard = ({ country, isTop }) => {
  const flag = FLAGS[country.country_code] || "🌍";
  const name = COUNTRY_NAMES[country.country_code] || country.country_code;
  return (
    <div
      data-testid={`country-card-${country.country_code}`}
      className={`p-4 border ${
        isTop ? "border-[#7C3AED] bg-[#7C3AED]/5" : "border-zinc-200"
      } space-y-3`}
    >
      <div className="flex items-baseline justify-between">
        <div>
          <span className="text-2xl mr-2">{flag}</span>
          <span className="font-bold">{name}</span>
          <span className="ml-2 text-xs text-zinc-500 font-mono">#{country.rank}</span>
        </div>
        <div className="text-2xl font-black tracking-tight">
          {country.overall_score?.toFixed?.(1) ?? "?"}
          <span className="text-xs text-zinc-400 font-mono ml-1">/100</span>
        </div>
      </div>
      <div className="space-y-2">
        {(country.signal_breakdown || []).map((b, i) => (
          <SignalBar
            key={`${country.country_code}-${b.signal_key}-${i}`}
            label={b.signal_key}
            score={b.normalized_score}
            weight={b.weight}
            raw={b.raw_value}
            source={b.source}
            year={b.year}
            proxy={b.proxy}
          />
        ))}
      </div>
    </div>
  );
};

const CountryMatchPanel = ({ countries = [], computedAt }) => {
  if (!countries.length) {
    return (
      <div className="p-4 border border-zinc-200 bg-zinc-50 text-sm text-zinc-500" data-testid="country-match-empty">
        No country recommendations cached for this opportunity yet.
      </div>
    );
  }
  return (
    <section data-testid="country-match-panel" className="space-y-3">
      <header>
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900">
          Country Match · Algorithm Score
        </h3>
        <p className="text-xs text-zinc-500">
          Weighted z-score of 5 free-source signals (World Bank · EF EPI · OECD proxy).
        </p>
      </header>
      <div className="space-y-3">
        {countries.map((c, idx) => (
          <CountryCard key={c.country_code} country={c} isTop={idx === 0} />
        ))}
      </div>
      {computedAt && (
        <p className="text-[10px] text-zinc-400 font-mono">
          Signals last synced from World Bank / EF / OECD; analysis cached {computedAt}.
        </p>
      )}
    </section>
  );
};

export default CountryMatchPanel;
